import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";
import { LeadSource } from "./leadsource";

// ✅ Token Headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ✅ Reusable Error Handler
const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ Type: LeadGivenBy
export interface LeadGivenBy {
  leadgivenby_id?: string;
  LeadGivenBy_name: string;
  Lead_status: string;
  created_at?: string;
  client?: {
    client_id: string;
  };
}

// ✅ CREATE Lead Given By
export const createLeadGivenBy = async (
  data: { LeadGivenBy_name: string; Lead_status?: string }
): Promise<LeadGivenBy | null> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadGivenBy_name", data.LeadGivenBy_name);
    form.append("Lead_status", data.Lead_status || "Active");

    const res = await axios.post(`${API_BASE_PATH}/lead_given_by_create/`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Lead Given By created successfully!");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Lead Given By";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ GET ALL Lead Given By
export const fetchAllLeadGivenBy = async (): Promise<LeadGivenBy[]> => {
  try {
    const res = await axios.get(`${API_BASE_PATH}/leadGivenBy/getall/table/data`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Given By";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ GET ALL Lead Given By - Active Only
export const fetchAllLeadGivenByActive = async (): Promise<LeadGivenBy[]> => {
  try {
    const res = await axios.get(`${API_BASE_PATH}/leadGivenBy/getall/table/data/active`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Given By";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ UPDATE Lead Given By
export const updateLeadGivenBy = async (
  leadgivenby_id: string,
  data: { LeadGivenBy_name: string; Lead_status?: string }
): Promise<boolean> => {
  try {
    const form = new URLSearchParams();
    form.append("LeadGivenBy_name", data.LeadGivenBy_name);
    form.append("Lead_status", data.Lead_status || "Active");

    await axios.put(`${API_BASE_PATH}/leadGivenBy/${leadgivenby_id}/update`, form, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    toast.success("Lead Given By updated successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update Lead Given By";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ DELETE Lead Given By
export const deleteLeadGivenBy = async (leadgivenby_id: string): Promise<boolean> => {
  try {
    await axios.delete(`${API_BASE_PATH}/leadGivenBy/${leadgivenby_id}/delete`, {
      headers: getAuthHeaders(),
    });

    toast.success("Lead Given By deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete Lead Given By";
    toast.error(errorMessage);
    return false;
  }
};


/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Lead Given By – Excel
export const exportLeadGivenByExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_given_by_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead Given By exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export Lead Given By (Excel)");
    throw error;
  }
};

// ✅ Export all Lead Given By – PDF
export const exportLeadGivenByPDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_given_by_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead Given By exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export Lead Given By (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Lead Given By – Excel
export const exportSelectedLeadGivenByExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_given_by_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success(
      "Selected Lead Given By exported as Excel successfully"
    );
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected Lead Given By (Excel)"
    );
    throw error;
  }
};

// ✅ Export selected Lead Given By – PDF
export const exportSelectedLeadGivenByPDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/lead_given_by_all_export_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success(
      "Selected Lead Given By exported as PDF successfully"
    );
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected Lead Given By (PDF)"
    );
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Lead Given By
export const deleteLeadGivenByBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/lead_given_by_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    toast.success(
      res.data?.message ||
        "Lead Given By records deleted successfully"
    );
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error(
        "You are not authorized to delete Lead Given By records."
      );
    } else {
      handleAxiosError(error, "Failed to delete Lead Given By");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface LeadGivenByImportResult {
  message: string;
  added: {
    lead_given_by_name: string;
    lead_given_by_status: string;
  }[];
  skipped: {
    row: number;
    lead_given_by_name: string;
    reason: string;
  }[];
}

// ✅ Import Lead Given By Excel
export const importLeadGivenByExcel = async (
  file: File
): Promise<LeadGivenByImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/lead_given_by_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(
      res.data?.message ||
        "Lead Given By import completed"
    );
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import Lead Given By");
    throw error;
  }
};